import { useState } from 'react';

const TOKEN_PATHS = new Set([
  '/reset-password', '/verify-email', '/intermediary/setup-password',
  '/intermediary/reset-password', '/complete-credit-intermediary-registration',
]);
const credentialsByPath = new Map();
const storageKey = (path) => `mid_url_credentials:${path}`;

function canonicalAuthPath(path) {
  const canonical = path.toLowerCase().replace(/\/+$/, '') || '/';
  return TOKEN_PATHS.has(canonical) || canonical === '/auth/callback' ? canonical : path;
}

function credentialsFromUrl(path) {
  const { pathname, search, hash } = window.location;
  if (path === '/auth/callback') {
    const params = new URLSearchParams(hash.slice(1));
    // The broker's session id is opaque: preserve '+' and '%' as received.
    const sessionId = hash.match(/(?:^#|&)session_id=([^&]*)/)?.[1];
    return sessionId || params.has('error') ? {
      session_id: sessionId, error: params.get('error'), error_description: params.get('error_description'),
    } : null;
  }
  if (canonicalAuthPath(pathname) !== path) return null;
  const token = new URLSearchParams(search).get('token');
  return TOKEN_PATHS.has(path) && token ? { token } : null;
}

export function storeUrlCredentials(path, credentials) {
  credentialsByPath.set(path, credentials);
  try {
    sessionStorage.setItem(storageKey(path), JSON.stringify(credentials));
  } catch (_) {
    // Storage blocked: the current mount still works from memory.
  }
}

export function readUrlCredentials(path) {
  const fromUrl = credentialsFromUrl(path);
  if (fromUrl) return fromUrl;
  if (credentialsByPath.has(path)) return credentialsByPath.get(path);
  try {
    return JSON.parse(sessionStorage.getItem(storageKey(path))) || {};
  } catch (_) {
    return {};
  }
}

export function clearUrlCredentials(path) {
  credentialsByPath.delete(path);
  try {
    sessionStorage.removeItem(storageKey(path));
  } catch (_) {
    // Storage may be disabled.
  }
}


export function clearInvalidUrlCredentials(path, status, detail) {
  const message = typeof detail === 'string' ? detail : detail?.code || detail?.error_description || '';
  if (status >= 400 && status < 500 && /token|link|session|sess[aã]o|state/i.test(message)
    && /inv[aá]lid|expir|already.used|j[aá].*(?:usado|utilizado)/i.test(message)) clearUrlCredentials(path);
}

// Called before createRoot; the router and consent effects see only the clean URL.
export function captureAuthUrlTokens() {
  const { pathname, search, hash } = window.location;
  const oauth = credentialsFromUrl('/auth/callback');
  const path = oauth ? '/auth/callback' : canonicalAuthPath(pathname);
  const credentials = oauth || credentialsFromUrl(path);
  if (!credentials) return;
  // Legacy OAuth fragments are scrubbed and discarded; they are no longer valid credentials.
  if (credentials && path !== '/auth/callback') storeUrlCredentials(path, credentials);
  if (path === '/auth/callback') clearUrlCredentials(path);
  if ((search || hash) && pathname + search + hash !== path) window.history.replaceState(window.history.state, '', path);
}

export function useUrlToken(path) {
  const [token] = useState(() => readUrlCredentials(path).token || null);
  return token;
}

export function sanitizeRecordingUrl(url) {
  if (typeof url !== 'string') return url;
  return url.split(/[?#]/)[0]
    .replace(/(\/(?:validate-token|validate-setup-token|validate-reset-token))\/[^/]+/g, '$1');
}
